import {
  Description,
  FieldError,
  Input,
  Label,
  ListBox,
  Select,
  TextField,
} from "@heroui/react";
import { uniqBy } from "lodash";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { BannerProductPicker } from "./banner-product-picker";

import { ComboField } from "@/components/kit";
import { useGetAllBrands } from "@/hooks/api/brand/useGetAllBrands";
import { useGetAllCategories } from "@/hooks/api/category/useGetAllCategories";
import { useGetAllCountries } from "@/hooks/api/country/useGetAllCountries";
import { BannerFormData } from "@/schemas/banner-form.schema";
import { BANNER_LINK_TYPES, BannerLinkType } from "@/types";
import {
  BANNER_TARGET_KINDS,
  BannerTargetKind,
  buildBannerLink,
  changeBannerTargetKind,
} from "@/utils/banner-link";

export const BANNER_TARGET_KIND_LABELS: Record<BannerTargetKind, string> = {
  product: "A product",
  products: "Product list",
  search: "Search results",
  brands: "All brands",
  localBrands: "Local brands",
  countries: "Countries",
  categories: "Categories tab",
  cart: "Cart",
  specialItems: "Cart with special items",
  website: "Website",
  custom: "Custom link",
};

export const BANNER_LINK_TYPE_LABELS: Record<BannerLinkType, string> = {
  "deep-link": "Deep link",
  "web-link": "Web link",
};

const LINK_TYPE_DESCRIPTIONS: Record<BannerLinkType, string> = {
  "deep-link": "Opens a screen inside the app",
  "web-link": "Opens in the browser, must be a full URL",
};

const ProductsFilterFields = () => {
  const form = useFormContext<BannerFormData>();
  const categoryId = useWatch({
    control: form.control,
    name: "target.categoryId",
  });

  const { data: categories = [] } = useGetAllCategories();
  const { data: brands = [] } = useGetAllBrands();
  const { data: countries = [] } = useGetAllCountries();

  const selectedCategory = categories.find(
    (category) => category.id === categoryId,
  );
  const subCategories = selectedCategory
    ? selectedCategory.subCategories
    : uniqBy(
        categories.flatMap((category) => category.subCategories),
        "id",
      );

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Controller
        control={form.control}
        name="target.categoryId"
        render={({ field }) => (
          <ComboField
            items={categories}
            label="Category"
            placeholder="Any"
            value={field.value}
            onBlur={field.onBlur}
            onChange={(id) => {
              field.onChange(id);
              form.setValue("target.subCategoryId", "", { shouldDirty: true });
            }}
          />
        )}
      />
      <Controller
        control={form.control}
        name="target.subCategoryId"
        render={({ field }) => (
          <ComboField
            items={subCategories}
            label="Subcategory"
            placeholder="Any"
            value={field.value}
            onBlur={field.onBlur}
            onChange={field.onChange}
          />
        )}
      />
      <Controller
        control={form.control}
        name="target.brandId"
        render={({ field }) => (
          <ComboField
            items={brands}
            label="Brand"
            placeholder="Any"
            value={field.value}
            onBlur={field.onBlur}
            onChange={field.onChange}
          />
        )}
      />
      <Controller
        control={form.control}
        name="target.countryId"
        render={({ field }) => (
          <ComboField
            items={countries}
            label="Country"
            placeholder="Any"
            value={field.value}
            onBlur={field.onBlur}
            onChange={field.onChange}
          />
        )}
      />
    </div>
  );
};

/** A text field wired to one react-hook-form path of the banner target. */
const TargetTextField = ({
  name,
  label,
  placeholder,
  description,
  isMono,
}: {
  name: "target.searchTerm" | "target.url" | "target.promoCode" | "target.link";
  label: string;
  placeholder?: string;
  description?: string;
  isMono?: boolean;
}) => {
  const form = useFormContext<BannerFormData>();

  return (
    <Controller
      control={form.control}
      name={name}
      render={({ field, fieldState }) => (
        <TextField
          className="flex flex-col gap-1.5"
          isInvalid={fieldState.invalid}
          name={field.name}
          value={(field.value as string | undefined) ?? ""}
          onBlur={field.onBlur}
          onChange={field.onChange}
        >
          <Label>{label}</Label>
          <Input
            ref={field.ref}
            className={isMono ? "font-mono text-xs" : undefined}
            placeholder={placeholder}
            variant="secondary"
          />
          {description ? <Description>{description}</Description> : null}
          <FieldError>{fieldState.error?.message}</FieldError>
        </TextField>
      )}
    />
  );
};

const CustomLinkFields = () => {
  const form = useFormContext<BannerFormData>();
  const linkType = useWatch({ control: form.control, name: "target.linkType" });

  return (
    <>
      <Controller
        control={form.control}
        name="target.linkType"
        render={({ field, fieldState }) => (
          <Select
            className="flex flex-col gap-1.5"
            isInvalid={fieldState.invalid}
            selectedKey={field.value}
            variant="secondary"
            onBlur={field.onBlur}
            onSelectionChange={(key) => {
              const selected = BANNER_LINK_TYPES.find((type) => type === key);

              if (selected) field.onChange(selected);
            }}
          >
            <Label>Link type</Label>
            <Select.Trigger>
              <Select.Value />
              <Select.Indicator />
            </Select.Trigger>
            <FieldError>{fieldState.error?.message}</FieldError>
            <Select.Popover>
              <ListBox>
                {BANNER_LINK_TYPES.map((type) => (
                  <ListBox.Item
                    key={type}
                    id={type}
                    textValue={BANNER_LINK_TYPE_LABELS[type]}
                  >
                    {BANNER_LINK_TYPE_LABELS[type]}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                ))}
              </ListBox>
            </Select.Popover>
          </Select>
        )}
      />
      <TargetTextField
        description={LINK_TYPE_DESCRIPTIONS[linkType]}
        label="Link"
        name="target.link"
        placeholder={linkType === "web-link" ? "https://" : "dukkanti://..."}
      />
    </>
  );
};

export const BannerLinkBuilder = () => {
  const form = useFormContext<BannerFormData>();
  const target = useWatch({ control: form.control, name: "target" });
  const savedLink = buildBannerLink(target);

  const handleKindChange = (kind: BannerTargetKind) => {
    form.clearErrors("target");
    form.setValue("target", changeBannerTargetKind(target, kind), {
      shouldDirty: true,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <ComboField
        items={BANNER_TARGET_KINDS.map((kind) => ({
          id: kind,
          name: BANNER_TARGET_KIND_LABELS[kind],
        }))}
        label="When tapped, open"
        placeholder="Search destinations"
        value={target.kind}
        onChange={(id) => {
          const selected = BANNER_TARGET_KINDS.find((kind) => kind === id);

          if (selected && selected !== target.kind) handleKindChange(selected);
        }}
      />

      {target.kind === "product" && (
        <Controller
          control={form.control}
          name="target.productId"
          render={({ field, fieldState }) => (
            <BannerProductPicker
              errorMessage={fieldState.error?.message}
              productId={field.value}
              onBlur={field.onBlur}
              onChange={field.onChange}
            />
          )}
        />
      )}

      {target.kind === "products" && <ProductsFilterFields />}

      {target.kind === "search" && (
        <TargetTextField
          label="Search term"
          name="target.searchTerm"
          placeholder="e.g. milk"
        />
      )}

      {target.kind === "website" && (
        <TargetTextField
          description="Opens in the browser"
          label="Website URL"
          name="target.url"
          placeholder="https://"
        />
      )}

      {target.kind === "custom" && <CustomLinkFields />}

      {"promoCode" in target && (
        <TargetTextField
          description="Applies this coupon when the banner is tapped"
          label="Promo code"
          name="target.promoCode"
          placeholder="Optional"
        />
      )}

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium" id="saved-link-label">
            Saved link
          </span>
          <span className="bg-surface-secondary text-muted rounded-md px-1.5 py-0.5 text-xs">
            {BANNER_LINK_TYPE_LABELS[savedLink.linkType]}
          </span>
        </div>
        <textarea
          readOnly
          aria-labelledby="saved-link-label"
          className="bg-surface-secondary min-h-16 w-full resize-none rounded-xl px-3 py-2 font-mono text-xs break-all outline-none"
          rows={3}
          value={savedLink.link}
        />
      </div>
    </div>
  );
};
